import { useEffect, useState } from "react";
import { Check, X, Trash2 } from "lucide-react";
import api, { mediaUrl, apiError } from "@/lib/api";
import StarRating from "@/components/StarRating";
import { toast } from "sonner";

const FILTERS = [
    { v: "pending", l: "Pending" },
    { v: "approved", l: "Approved" },
    { v: "rejected", l: "Rejected" },
    { v: "", l: "All" },
];

export default function AdminReviews() {
    const [reviews, setReviews] = useState([]);
    const [status, setStatus] = useState("pending");
    const [mediaType, setMediaType] = useState("");

    const load = () => {
        const sp = new URLSearchParams();
        if (status) sp.set("status", status);
        if (mediaType) sp.set("media_type", mediaType);
        api.get(`/admin/reviews?${sp}`).then(({ data }) => setReviews(data.reviews));
    };
    useEffect(() => { load(); }, [status, mediaType]);

    const moderate = async (id, s) => {
        try { await api.put(`/admin/reviews/${id}/status`, { status: s }); toast.success(`Review ${s}`); load(); }
        catch (err) { toast.error(apiError(err)); }
    };
    const remove = async (id) => {
        if (!window.confirm("Delete this review permanently?")) return;
        try { await api.delete(`/admin/reviews/${id}`); toast.success("Review deleted"); load(); }
        catch (err) { toast.error(apiError(err)); }
    };

    return (
        <div>
            <h1 className="text-2xl font-semibold mb-6">Review Moderation</h1>
            <div className="flex flex-wrap gap-3 mb-6">
                <div className="flex gap-1 bg-white border border-gray-200 rounded p-1">
                    {FILTERS.map((f) => <button key={f.v} data-testid={`review-filter-${f.l.toLowerCase()}`} onClick={() => setStatus(f.v)} className={`px-3 py-1.5 text-sm rounded ${status === f.v ? "bg-ink text-white" : "text-gray-500"}`}>{f.l}</button>)}
                </div>
                <select value={mediaType} onChange={(e) => setMediaType(e.target.value)} className="border border-gray-300 rounded px-3 text-sm bg-white">
                    <option value="">All media</option>
                    <option value="photo">Photo reviews</option>
                    <option value="video">Video reviews</option>
                </select>
            </div>

            {reviews.length === 0 ? <p className="text-gray-400 py-10 text-center">No reviews.</p> : (
                <div className="grid md:grid-cols-2 gap-4">
                    {reviews.map((r) => (
                        <div key={r.id} className="bg-white border border-gray-200 rounded-lg p-5" data-testid={`admin-review-${r.id}`}>
                            <div className="flex justify-between items-start">
                                <div>
                                    <StarRating value={r.rating} />
                                    <p className="text-sm font-medium mt-1">{r.customer_name} {r.verified_purchase && <span className="text-[10px] text-gold uppercase ml-1">Verified</span>}</p>
                                    <p className="text-xs text-gray-400">{r.product_name} · {(r.created_at || "").slice(0, 10)}</p>
                                </div>
                                <span className={`text-xs px-2 py-1 rounded ${r.status === "approved" ? "bg-green-100 text-green-700" : r.status === "rejected" ? "bg-red-100 text-red-600" : "bg-yellow-100 text-yellow-700"}`}>{r.status}</span>
                            </div>
                            {r.text && <p className="text-sm text-gray-600 mt-3">{r.text}</p>}
                            {r.media?.length > 0 && (
                                <div className="flex flex-wrap gap-2 mt-3">
                                    {r.media.map((m, i) => m.kind === "video" ?
                                        <video key={i} src={mediaUrl(m.url)} controls preload="none" className="h-24 rounded bg-black" /> :
                                        <img key={i} src={mediaUrl(m.url)} alt="" className="h-20 w-20 object-cover rounded" />)}
                                </div>
                            )}
                            <div className="flex gap-2 mt-4">
                                {r.status !== "approved" && <button data-testid={`approve-review-${r.id}`} onClick={() => moderate(r.id, "approved")} className="flex items-center gap-1 bg-green-600 text-white px-3 py-1.5 rounded text-xs"><Check className="h-3.5 w-3.5" /> Approve</button>}
                                {r.status !== "rejected" && <button data-testid={`reject-review-${r.id}`} onClick={() => moderate(r.id, "rejected")} className="flex items-center gap-1 bg-gray-200 px-3 py-1.5 rounded text-xs"><X className="h-3.5 w-3.5" /> Reject</button>}
                                <button onClick={() => remove(r.id)} className="flex items-center gap-1 text-red-500 px-3 py-1.5 rounded text-xs ml-auto"><Trash2 className="h-3.5 w-3.5" /> Delete</button>
                            </div>
                        </div>
                    ))}
                </div>
            )}
        </div>
    );
}
